import React, { useState, useEffect } from 'react';
import { Card } from './ui/Card';
import { Button, PrimaryButton, SecondaryButton } from './ui/Button';
import { FileText, Shield, CheckCircle, AlertTriangle, Upload, Clock, Settings } from 'lucide-react';
import apiConfig from '../config/apiConfig';
import { authHeaders } from '../services/authHeaders';

const PolicyEvaluationPage = ({ sessionId, onComplete, onSkip }) => {
  const [policyTypes, setPolicyTypes] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [evaluationResults, setEvaluationResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [industryLocked, setIndustryLocked] = useState(false);

  useEffect(() => {
    // Load session info and policy configuration
    loadSessionAndConfiguration();
  }, [sessionId]);

  const loadSessionAndConfiguration = async () => {
    try {
      setLoading(true);
      
      // First, get session info including industry from coordinator's input
      if (sessionId) {
        try {
          const sessionResponse = await fetch(`${apiConfig.getApiBaseUrl()}/foundations/session/${sessionId}/info`, { headers: authHeaders() });
          const sessionData = await sessionResponse.json();
          
          if (sessionData.success && sessionData.session) {
            const { industry, companyName: companyNameFromSession } = sessionData.session;
            
            // Map common industry variations to API values
            const industryMapping = {
              'Technology': 'technology',
              'Healthcare': 'healthcare',
              'Finance': 'finance',
              'Retail': 'retail',
              'Manufacturing': 'manufacturing',
              'Energy': 'energy',
              'Transportation': 'transportation',
              'Real Estate': 'realestate',
              'Education': 'education',
              'General': 'general'
            };
            
            const mappedIndustry = industryMapping[industry] || industry?.toLowerCase() || 'technology';
            setSelectedIndustry(mappedIndustry);
            setCompanyName(companyNameFromSession || '');
            setIndustryLocked(true); // Lock industry selection since it comes from assessment
            
            console.log(`📊 Session ${sessionId}: Company "${companyNameFromSession}" in ${industry} industry`);
          }
        } catch (error) {
          console.error('Error fetching session info:', error);
          // Continue with default industry if session fetch fails
          setSelectedIndustry('technology');
        }
      }
      
      // Load policy types from production backend
      const policyTypesResponse = await fetch(`${apiConfig.getApiBaseUrl()}/documents/policy-types`);
      const policyTypesData = await policyTypesResponse.json();
      
      // Load industries from production backend  
      const industriesResponse = await fetch(`${apiConfig.getApiBaseUrl()}/documents/industries`);
      const industriesData = await industriesResponse.json();
      
      if (policyTypesData.success) {
        setPolicyTypes(policyTypesData.policyTypes);
      }
      
      if (industriesData.success) {
        setIndustries(industriesData.industries);
      }
      
    } catch (error) {
      console.error('Error loading configuration:', error);
      // Fallback to basic configuration if API fails
      setPolicyTypes([
        { id: 'training', name: 'Cybersecurity Training Program', description: 'Training and awareness policies', framework: 'NIST SP 800-50', icon: '🎓' },
        { id: 'incident_response', name: 'Incident Response Plan', description: 'Incident response procedures', framework: 'NIST 800-61', icon: '🚨' }
      ]);
      setIndustries([
        { id: 'technology', name: 'Technology', description: 'SaaS, cloud, customer data' },
        { id: 'general', name: 'General', description: 'Standard NIST framework' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyUpload = async (event, policyTypeId) => {
    const file = event.target.files[0];
    if (file && policyTypeId) {
      const policyType = policyTypes.find(p => p.id === policyTypeId);
      console.log(`📄 Uploading ${policyType.name}:`, file.name, `(Industry: ${selectedIndustry})`);
      
      try {
        setLoading(true);
        
        // Update upload status to uploading
        setUploadStatus(prev => ({
          ...prev,
          [policyTypeId]: { status: 'uploading', fileName: file.name }
        }));
        
        // Create FormData for file upload with production backend format
        const formData = new FormData();
        formData.append('document', file);
        formData.append('policyType', policyTypeId);
        formData.append('industry', selectedIndustry);
        formData.append('sessionId', sessionId);
        
        // Upload to Foundations backend with production evaluation
        const response = await fetch(`${apiConfig.getApiBaseUrl()}/documents/upload`, {
          method: 'POST',
          // Authorization only — the browser must set the multipart Content-Type + boundary.
          headers: authHeaders(),
          body: formData
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log(`✅ ${policyType.name} evaluation completed successfully`);
          console.log('📊 Evaluation Results:', result);
          
          // Update status to completed with production results
          setUploadStatus(prev => ({
            ...prev,
            [policyTypeId]: { 
              status: 'completed', 
              fileName: file.name,
              uploadDate: new Date().toISOString()
            }
          }));
          
          // Store production evaluation results
          setEvaluationResults(prev => ({
            ...prev,
            [policyTypeId]: {
              overallScore: result.overallScore,
              categories: result.categories,
              recommendations: result.recommendations || [],
              metadata: result.metadata,
              industryContext: result.industryContext,
              focusAreas: result.focusAreas,
              threatLandscape: result.threatLandscape,
              framework: result.metadata?.framework || policyType.framework
            }
          }));
          
        } else {
          console.error(`❌ ${policyType.name} upload failed:`, result.error);
          setUploadStatus(prev => ({
            ...prev,
            [policyTypeId]: { status: 'failed', error: result.error }
          }));
          alert(`Failed to upload ${policyType.name}: ${result.error}`);
        }
      } catch (error) {
        console.error(`❌ Upload error for ${policyType.name}:`, error);
        setUploadStatus(prev => ({
          ...prev,
          [policyTypeId]: { status: 'failed', error: error.message }
        }));
        alert(`Failed to upload ${policyType.name}: ${error.message}`);
      } finally {
        setLoading(false);
        // Clear the file input
        event.target.value = '';
      }
    }
  };

  const handleComplete = () => {
    const completedEvaluations = Object.keys(evaluationResults).length;
    onComplete({
      sessionId,
      evaluationsCompleted: completedEvaluations,
      selectedIndustry,
      results: evaluationResults
    });
  };

  const getOverallProgress = () => {
    const completed = Object.values(uploadStatus).filter(s => s.status === 'completed').length;
    return Math.round((completed / policyTypes.length) * 100);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#e6f1ff] mb-2">
          Policy Evaluation & Compliance Analysis
        </h1>
        <p className="text-[#a8b2d1]">
          Upload your organization's security policies for comprehensive evaluation against industry frameworks.
        </p>
      </div>

      {/* Industry Configuration - Auto-populated from Assessment */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Settings aria-hidden="true" className="mr-2" size={20} />
            Assessment Configuration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#ccd6f6] mb-2">
                Company Name
              </label>
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.12)] rounded-lg">
                <span className="text-[#e6f1ff] font-medium">{companyName || 'Loading...'}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#ccd6f6] mb-2">
                Industry Framework
              </label>
              {industryLocked ? (
                <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.12)] rounded-lg">
                  <span className="text-[#e6f1ff] font-medium">
                    {industries.find(i => i.id === selectedIndustry)?.name || selectedIndustry.charAt(0).toUpperCase() + selectedIndustry.slice(1)}
                  </span>
                  <p className="text-xs text-[#8892b0] mt-1">
                    Auto-selected from coordinator assessment
                  </p>
                </div>
              ) : (
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  aria-label="Industry framework"
                  className="w-full p-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-transparent"
                  disabled={loading}
                >
                  {industries.map(industry => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <p className="text-sm text-[#8892b0] mt-4">
            Policy evaluation will use the {industries.find(i => i.id === selectedIndustry)?.name || selectedIndustry} industry framework based on your assessment profile.
          </p>
        </div>
      </Card>

      {/* Policy Types Section */}
      <Card className="mb-6">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <Shield aria-hidden="true" className="mr-2" size={20} />
              Policy Document Evaluation ({Object.keys(evaluationResults).length}/{policyTypes.length})
            </h2>
            {policyTypes.length > 0 && (
              <div className="text-right">
                <div className="text-sm text-[#a8b2d1]">Overall Progress</div>
                <div className="text-lg font-semibold text-blue-600">{getOverallProgress()}%</div>
              </div>
            )}
          </div>
          <p className="text-[#a8b2d1] mb-6">
            Upload policy documents for evaluation against {selectedIndustry} industry frameworks:
          </p>
          
          {loading && policyTypes.length === 0 ? (
            <div className="text-center py-8">
              <Clock aria-hidden="true" className="mx-auto animate-spin mb-2" size={24} />
              <p>Loading policy types...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {policyTypes.map(policyType => {
                const status = uploadStatus[policyType.id];
                const result = evaluationResults[policyType.id];
                
                const getStatusIcon = () => {
                  if (!status) return <FileText aria-hidden="true" className="text-[#5a6a8a]" size={20} />;
                  
                  switch (status.status) {
                    case 'completed':
                      return <CheckCircle aria-hidden="true" className="text-green-500" size={20} />;
                    case 'uploading':
                      return <Clock aria-hidden="true" className="text-blue-500 animate-spin" size={20} />;
                    case 'failed':
                      return <AlertTriangle aria-hidden="true" className="text-red-500" size={20} />;
                    default:
                      return <FileText aria-hidden="true" className="text-[#5a6a8a]" size={20} />;
                  }
                };
                
                return (
                  <div key={policyType.id} className="border rounded-lg p-4 hover:shadow-[0_4px_24px_rgba(0,0,0,0.3)] transition-shadow ra-glass">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-lg mr-2">{policyType.icon}</span>
                          {getStatusIcon()}
                          <h3 className="font-semibold ml-2 text-sm">{policyType.name}</h3>
                        </div>
                        <p className="text-xs text-[#a8b2d1] mb-2">{policyType.description}</p>
                        <p className="text-xs text-blue-600 font-medium">{policyType.framework}</p>
                        {status?.fileName && (
                          <p className="text-xs text-green-600 mt-1">📄 {status.fileName}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm">
                        {!status && (
                          <span className="text-[#8892b0]">Ready to upload</span>
                        )}
                        {status?.status === 'uploading' && (
                          <span className="text-blue-600">Processing...</span>
                        )}
                        {status?.status === 'completed' && result && (
                          <span className="text-green-600 font-semibold">Score: {result.overallScore}%</span>
                        )}
                        {status?.status === 'failed' && (
                          <span className="text-red-600">Upload failed</span>
                        )}
                      </div>
                    </div>
                    
                    {(!status || status.status === 'failed') && (
                      <div className="mb-3">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={(e) => handlePolicyUpload(e, policyType.id)}
                          className="hidden"
                          id={`upload-${policyType.id}`}
                          disabled={loading}
                        />
                        <label
                          htmlFor={`upload-${policyType.id}`}
                          className="w-full inline-flex items-center justify-center px-3 py-2 border border-blue-500 text-blue-500 rounded-md cursor-pointer hover:bg-blue-50 transition-colors text-sm"
                        >
                          <Upload aria-hidden="true" className="mr-2" size={14} />
                          {status?.status === 'failed' ? 'Retry Upload' : 'Upload Document'}
                        </label>
                      </div>
                    )}
                    
                    {result && (
                      <div className="mt-4 p-3 bg-[rgba(255,255,255,0.03)] rounded">
                        <div className="mb-3">
                          <h4 className="font-medium text-sm text-[#ccd6f6] mb-2">
                            Evaluation Results
                          </h4>
                          <div className="text-2xl font-bold text-blue-600 mb-2">
                            {result.overallScore}% Compliance
                          </div>
                          <p className="text-xs text-[#a8b2d1]">
                            Framework: {result.framework}
                          </p>
                        </div>
                        
                        {result.categories && Object.keys(result.categories).length > 0 && (
                          <div className="mb-3">
                            <h5 className="text-xs font-medium text-[#ccd6f6] mb-2">Category Scores</h5>
                            <div className="space-y-1">
                              {Object.entries(result.categories).slice(0, 3).map(([category, details]) => (
                                <div key={category} className="flex justify-between text-xs">
                                  <span className="truncate pr-2">{details.name || category}</span>
                                  <span className="font-medium text-blue-600">{details.score ?? 0}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {result.recommendations && result.recommendations.length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-[#ccd6f6] mb-2">Key Recommendations</h5>
                            <ul className="text-xs text-[#a8b2d1] space-y-1">
                              {result.recommendations.slice(0, 2).map((rec, idx) => (
                                <li key={idx} className="flex items-start">
                                  <CheckCircle aria-hidden="true" className="mr-1 mt-0.5 flex-shrink-0" size={10} />
                                  <span className="truncate">
                                    {typeof rec === 'string' ? rec : rec.recommendation || rec.toString()}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Summary Section */}
      {Object.keys(evaluationResults).length > 0 && (
        <Card className="mb-6">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">
              Evaluation Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {Math.round(
                    Object.values(evaluationResults).reduce((sum, r) => sum + r.overallScore, 0) / 
                    Object.values(evaluationResults).length
                  )}%
                </div>
                <p className="text-sm text-[#a8b2d1]">Average Score</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {Object.keys(evaluationResults).length}
                </div>
                <p className="text-sm text-[#a8b2d1]">Policies Evaluated</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {selectedIndustry.charAt(0).toUpperCase() + selectedIndustry.slice(1)}
                </div>
                <p className="text-sm text-[#a8b2d1]">Industry Framework</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between mt-8">
        <SecondaryButton onClick={onSkip}>
          Skip Policy Evaluation
        </SecondaryButton>
        <PrimaryButton 
          onClick={handleComplete}
          disabled={Object.keys(evaluationResults).length === 0}
        >
          Complete Evaluation ({Object.keys(evaluationResults).length} policies)
        </PrimaryButton>
      </div>

    </div>
  );
};

export default PolicyEvaluationPage;